import { t } from "../i18n";
import { useMemo, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import type { Setup, TablePage } from "../types";
import { useBackHandler } from "../backNavigation";

type Props = {
  setup: Setup;
  onClose: () => void;
  onSaveSetup: (next: Setup) => void;
};

type NamePrompt =
  | { mode: "add"; row: number; col: number; value: string }
  | { mode: "rename"; id: number; value: string };

// Drag starts once the finger has travelled this far, so a plain tap still renames.
const DRAG_SLOP = 6;

export function PagesDialog({ setup, onClose, onSaveSetup }: Props) {
  const pages = useMemo(
    () => setup.table_pages.slice().sort((a, b) => a.pos_row - b.pos_row || a.pos_col - b.pos_col || a.id - b.id),
    [setup.table_pages]
  );

  const [namePrompt, setNamePrompt] = useState<NamePrompt | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TablePage | null>(null);
  useBackHandler(true, onClose);
  useBackHandler(Boolean(namePrompt), () => setNamePrompt(null));
  useBackHandler(Boolean(confirmDelete), () => setConfirmDelete(null));
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const drag = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);

  const commit = (nextPages: TablePage[]): void => {
    onSaveSetup({ ...setup, table_pages: nextPages });
  };

  const pageAt = (row: number, col: number): TablePage | undefined =>
    pages.find((page) => page.pos_row === row && page.pos_col === col);

  // One spare row/column on every side so a page can be dragged outward, like the app.
  const bounds = useMemo(() => {
    if (!pages.length) {
      return { minRow: 0, maxRow: 0, minCol: 0, maxCol: 0 };
    }
    return {
      minRow: Math.min(...pages.map((page) => page.pos_row)) - 1,
      maxRow: Math.max(...pages.map((page) => page.pos_row)) + 1,
      minCol: Math.min(...pages.map((page) => page.pos_col)) - 1,
      maxCol: Math.max(...pages.map((page) => page.pos_col)) + 1
    };
  }, [pages]);

  // Swap with whatever already sits on the target cell, exactly as moveTablePageTo does.
  const movePageTo = (pageId: number, targetRow: number, targetCol: number): void => {
    const moving = pages.find((page) => page.id === pageId);
    if (!moving || (moving.pos_row === targetRow && moving.pos_col === targetCol)) {
      return;
    }
    const occupant = pageAt(targetRow, targetCol);
    commit(pages.map((page) => {
      if (page.id === moving.id) {
        return { ...page, pos_row: targetRow, pos_col: targetCol };
      }
      if (occupant && page.id === occupant.id) {
        return { ...page, pos_row: moving.pos_row, pos_col: moving.pos_col };
      }
      return page;
    }));
  };

  const cellUnder = (x: number, y: number): { row: number; col: number } | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-cell]");
    if (!el?.dataset.cell) {
      return null;
    }
    const [row, col] = el.dataset.cell.split(":").map(Number);
    return { row, col };
  };

  const handlePointerDown = (event: React.PointerEvent, page: TablePage): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { id: page.id, x: event.clientX, y: event.clientY, moved: false };
  };

  const handlePointerMove = (event: React.PointerEvent): void => {
    const state = drag.current;
    if (!state) {
      return;
    }
    if (!state.moved) {
      if (Math.abs(event.clientX - state.x) <= DRAG_SLOP && Math.abs(event.clientY - state.y) <= DRAG_SLOP) {
        return;
      }
      state.moved = true;
      setDraggingId(state.id);
    }
    const cell = cellUnder(event.clientX, event.clientY);
    setDragOver(cell ? `${cell.row}:${cell.col}` : null);
  };

  const handlePointerUp = (event: React.PointerEvent, page: TablePage): void => {
    const state = drag.current;
    drag.current = null;
    setDraggingId(null);
    setDragOver(null);
    if (!state) {
      return;
    }
    if (!state.moved) {
      setNamePrompt({ mode: "rename", id: page.id, value: page.name });
      return;
    }
    const cell = cellUnder(event.clientX, event.clientY);
    if (cell) {
      movePageTo(state.id, cell.row, cell.col);
    }
  };

  const addPage = (name: string, row: number, col: number): void => {
    const clean = name.trim();
    if (!clean) {
      return;
    }
    const id = pages.reduce((highest, page) => Math.max(highest, page.id), 0) + 1;
    commit([...pages, { id, name: clean, pos_row: row, pos_col: col }]);
  };

  const submitPrompt = (): void => {
    if (!namePrompt) {
      return;
    }
    const clean = namePrompt.value.trim();
    if (clean) {
      if (namePrompt.mode === "add") {
        addPage(clean, namePrompt.row, namePrompt.col);
      } else {
        commit(pages.map((page) => (page.id === namePrompt.id ? { ...page, name: clean } : page)));
      }
    }
    setNamePrompt(null);
  };

  const deletePage = (page: TablePage): void => {
    setConfirmDelete(null);
    if (pages.length <= 1) {
      return;
    }
    commit(pages.filter((entry) => entry.id !== page.id));
  };

  const rows: number[] = [];
  for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
    rows.push(row);
  }
  const cols: number[] = [];
  for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
    cols.push(col);
  }

  const nextFreeCol = pages.reduce((highest, page) => Math.max(highest, page.pos_col), -1) + 1;

  return (
    <div className="dialog-backdrop">
      <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Σελίδες τραπεζιών")}>
        <header className="dialog-header">
          <div>
            <h2>{t("Σελίδες τραπεζιών")}</h2>
            <p>{t("Τοποθέτησε τις σελίδες όπως τις οθόνες. Η θέση ορίζει προς τα πού γίνεται το swipe.")}</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label={t("Κλείσιμο")}>
            <X />
          </button>
        </header>

        <div className="dialog-scroll">
          <p className="hint-text">{t("Σύρε μια σελίδα για μετακίνηση. Πάτημα μετονομάζει. Το κόκκινο − διαγράφει. Πάτα ένα κενό + για νέα σελίδα σε εκείνη τη θέση.")}</p>

          <div
            className="page-arrange-grid"
            style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}
          >
            {rows.map((row) =>
              cols.map((col) => {
                const key = `${row}:${col}`;
                const page = pageAt(row, col);
                const over = dragOver === key ? " drag-over" : "";
                if (!page) {
                  return (
                    <button
                      type="button"
                      key={key}
                      data-cell={key}
                      className={`page-cell empty${over}`}
                      onClick={() => setNamePrompt({ mode: "add", row, col, value: "" })}
                      aria-label={t("Νέα σελίδα στη θέση {0}, {1}", row + 1, col + 1)}
                    >
                      +
                    </button>
                  );
                }
                return (
                  <div
                    key={key}
                    data-cell={key}
                    className={`page-cell${draggingId === page.id ? " dragging" : ""}${over}`}
                    onPointerDown={(event) => handlePointerDown(event, page)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={(event) => handlePointerUp(event, page)}
                    onPointerCancel={() => {
                      drag.current = null;
                      setDraggingId(null);
                      setDragOver(null);
                    }}
                  >
                    <span className="page-cell-name">{page.name}</span>
                    {pages.length > 1 ? (
                      <button
                        type="button"
                        className="page-cell-delete"
                        aria-label={t("Διαγραφή {0}", page.name)}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          setConfirmDelete(page);
                        }}
                      >
                        −
                      </button>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <footer className="dialog-actions">
          <button
            type="button"
            className="secondary-command"
            onClick={() => setNamePrompt({ mode: "add", row: 0, col: nextFreeCol, value: "" })}
          >
            <Plus />
            <span>{t("Νέα σελίδα")}</span>
          </button>
        </footer>
      </section>

      {namePrompt ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Όνομα σελίδας")}>
            <header>
              <h2>{namePrompt.mode === "add" ? t("Νέα σελίδα") : t("Μετονομασία σελίδας")}</h2>
            </header>
            <label className="field">
              <span>{t("Όνομα σελίδας")}</span>
              <input
                autoFocus
                value={namePrompt.value}
                onChange={(event) => setNamePrompt({ ...namePrompt, value: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    submitPrompt();
                  }
                }}
              />
            </label>
            <div className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setNamePrompt(null)}>{t("Άκυρο")}</button>
              <button type="button" className="primary-command" onClick={submitPrompt}>{t("Αποθήκευση")}</button>
            </div>
          </section>
        </div>
      ) : null}

      {confirmDelete ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Διαγραφή σελίδας")}>
            <header>
              <h2>{t("Διαγραφή σελίδας")}</h2>
              <p>{t("Να διαγραφεί η σελίδα \"")}{confirmDelete.name}";</p>
            </header>
            <div className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setConfirmDelete(null)}>{t("Άκυρο")}</button>
              <button type="button" className="primary-command" onClick={() => deletePage(confirmDelete)}>{t("Διαγραφή")}</button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
